<script lang="ts">
	import { onMount } from 'svelte';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { format } from 'svelte-i18n';
	import ChatWindow from './ChatWindow.svelte';
	import AppUpdateRequired from './AppUpdateRequired.svelte';
	import TokenTypeMenu from './TokenTypeMenu.svelte';
	import AISceneModalWrapper from './AISceneModalWrapper.svelte';

	let chatWindowComponent: ChatWindow;
	let headerMenuOpen = false;
	$: isAppVersionSupported =
		$aiSceneChatStore.agentHealth[$aiSceneChatStore.activeAgentId]?.isAppVersionSupported !== false;

	$: headerMenuItems = [
		{
			type: 'history',
			label: $format('history', { default: 'History' }),
			subtitle: $format('alert_heading_coming_soon', { default: 'Coming soon' }),
			icon: 'fas fa-clock-rotate-left',
			disabled: true,
		},
		{
			type: 'terms',
			label: $format('terms', { default: 'Terms and Conditions' }),
			icon: 'fas fa-circle-info',
		},
	];

	function handleClose() {
		headerMenuOpen = false;
		aiSceneChatStore.closeModal();
	}

	function onBeforeClose() {
		if (headerMenuOpen) {
			headerMenuOpen = false;
			return false;
		}
		return true;
	}

	function isWithinModalSurface(target: EventTarget | null) {
		return (
			target instanceof Element &&
			(target.closest('.modal-panel') !== null || target.closest('.modal-backdrop') !== null)
		);
	}

	function addInteractionGuards(lockedScrollY: number) {
		let touchStartedOnInput = false;
		let lastInputTouchY = 0;

		const onTouchStart = (event: TouchEvent) => {
			const target = event.target;
			if (target instanceof Element && target.closest('.rich-mention-input')) {
				touchStartedOnInput = true;
				lastInputTouchY = event.touches[0].clientY;
			}
		};

		const onTouchEnd = () => {
			touchStartedOnInput = false;
		};

		const onTouchMove = (event: TouchEvent) => {
			if (!isWithinModalSurface(event.target)) {
				event.preventDefault();
				return;
			}
			const inputEl =
				event.target instanceof Element
					? (event.target.closest('.rich-mention-input') as HTMLElement | null)
					: null;
			if (inputEl && inputEl.scrollHeight <= inputEl.clientHeight) {
				event.preventDefault();
				return;
			}
			if (inputEl && inputEl.scrollHeight > inputEl.clientHeight && touchStartedOnInput) {
				event.preventDefault();
				const clientY = event.touches[0].clientY;
				const deltaY = clientY - lastInputTouchY;
				lastInputTouchY = clientY;
				const maxScroll = inputEl.scrollHeight - inputEl.clientHeight;
				inputEl.scrollTop = Math.max(0, Math.min(maxScroll, inputEl.scrollTop - deltaY));
				return;
			}
			const messagesEl =
				event.target instanceof Element
					? (event.target.closest('.messages-container') as HTMLElement | null)
					: null;
			if (messagesEl && messagesEl.scrollHeight <= messagesEl.clientHeight) {
				event.preventDefault();
			}
		};

		const onWheel = (event: WheelEvent) => {
			if (!isWithinModalSurface(event.target)) {
				event.preventDefault();
			}
		};

		const onWindowScroll = () => {
			if (window.scrollY !== lockedScrollY) {
				window.scrollTo(0, lockedScrollY);
			}
		};

		document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
		document.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });
		document.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: true });
		document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
		document.addEventListener('wheel', onWheel, { capture: true, passive: false });
		window.addEventListener('scroll', onWindowScroll, { passive: true });

		return () => {
			document.removeEventListener('touchstart', onTouchStart, true);
			document.removeEventListener('touchend', onTouchEnd, true);
			document.removeEventListener('touchcancel', onTouchEnd, true);
			document.removeEventListener('touchmove', onTouchMove, true);
			document.removeEventListener('wheel', onWheel, true);
			window.removeEventListener('scroll', onWindowScroll);
		};
	}

	async function handleNewChat() {
		headerMenuOpen = false;
		await aiSceneChatStore.saveCurrentToHistory();
		aiSceneChatStore.clearHistory();
	}

	function toggleHeaderMenu(event: MouseEvent) {
		event.stopPropagation();
		headerMenuOpen = !headerMenuOpen;
	}

	function handleHeaderMenuSelect(event: CustomEvent<string>) {
		headerMenuOpen = false;
		if (event.detail === 'terms') {
			aiSceneChatStore.openPrivacyPolicy(true);
		}
	}

	onMount(() => {
		aiSceneChatStore.loadChatPreferences();
	});
</script>

<AISceneModalWrapper
	open={$aiSceneChatStore.isModalOpen}
	onClose={handleClose}
	enableScrollLock
	useHeaderSlot
	onTransitionDone={() => aiSceneChatStore.setChatModalOpenTransitionDone(true)}
	onScrollLockApply={addInteractionGuards}
	{onBeforeClose}
>
	<svelte:fragment slot="header">
		<div class="modal-header">
			<span class="modal-title">{$format('ai_chat_title', { default: 'Shelly Assistant' })}</span>
			<div class="header-actions">
				<button
					class="header-btn"
					on:click={handleNewChat}
					aria-label={$format('ai_chat_new_chat_button_aria_label', {
						default: 'New Chat',
					})}
					title={$format('ai_chat_new_chat_button_title', { default: 'New Chat' })}
				>
					<i class="fas fa-pen-to-square" />
				</button>
				<div class="menu-wrapper">
					<button
						class="header-btn"
						on:click={toggleHeaderMenu}
						aria-label={$format('ai_chat_menu_button_aria_label', {
							default: 'More options',
						})}
						title={$format('ai_chat_menu_button_title', { default: 'More options' })}
					>
						<i class="fas fa-ellipsis-vertical" />
					</button>
					{#if headerMenuOpen}
						<TokenTypeMenu
							placement="left"
							width={220}
							items={headerMenuItems}
							on:select={handleHeaderMenuSelect}
							on:close={() => (headerMenuOpen = false)}
						/>
					{/if}
				</div>
				<button class="header-btn close-btn" on:click={handleClose} aria-label="Close">
					<i class="fas fa-times" />
				</button>
			</div>
		</div>
	</svelte:fragment>

	{#if isAppVersionSupported}
		<ChatWindow bind:this={chatWindowComponent} />
	{:else}
		<AppUpdateRequired />
	{/if}
</AISceneModalWrapper>

<style>
	/* Header slot content (chat-specific). Scoped under wrapper's .ai-scene-modal .modal-panel. */
	:global(.ai-scene-modal .modal-panel .modal-header) {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 24px;
		flex-shrink: 0;
		background: transparent;
		position: relative;
		z-index: 10;
	}

	:global(.ai-scene-modal .modal-panel .modal-title) {
		font-size: 18px;
		font-weight: 600;
		color: var(--color-text-primary, #fff);
	}

	:global(.ai-scene-modal .modal-panel .header-actions) {
		display: flex;
		align-items: center;
		gap: 4px;
		position: relative;
		z-index: 11;
	}

	:global(.ai-scene-modal .modal-panel .header-btn) {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		background: transparent;
		border: none;
		border-radius: 8px;
		color: var(--color-text-primary, #fff);
		cursor: pointer;
		transition:
			background-color 0.2s,
			color 0.2s;
	}

	:global(.ai-scene-modal .modal-panel .header-btn:hover) {
		background: var(--color-background-3, #2a2a2a);
		color: var(--color-text-primary, #fff);
	}

	:global(.ai-scene-modal .modal-panel .header-btn i) {
		font-size: 16px;
	}

	:global(.ai-scene-modal .modal-panel .header-btn.disabled) {
		opacity: 0.5;
		cursor: not-allowed;
	}

	:global(.ai-scene-modal .modal-panel .menu-wrapper) {
		position: relative;
		z-index: 12;
	}

	/* Touch-action and overscroll: prevent scroll chaining to document when at messages top/bottom. */
	:global(.ai-scene-modal .modal-panel .messages-container) {
		touch-action: pan-y;
		overscroll-behavior: none;
	}
	:global(.ai-scene-modal .modal-panel .suggestions-chips),
	:global(.ai-scene-modal .modal-panel .suggestions-container) {
		touch-action: pan-x;
	}
	:global(.ai-scene-modal .modal-panel .mention-dropdown) {
		touch-action: pan-y;
	}
	:global(.ai-scene-modal .modal-panel .modal-header),
	:global(.ai-scene-modal .modal-panel .modal-title),
	:global(.ai-scene-modal .modal-panel .header-actions),
	:global(.ai-scene-modal .modal-panel .header-btn),
	:global(.ai-scene-modal .modal-panel .drag-handle),
	:global(.ai-scene-modal .modal-panel .chat-window),
	:global(.ai-scene-modal .modal-panel .bottom-section),
	:global(.ai-scene-modal .modal-panel .suggestions-anchor),
	:global(.ai-scene-modal .modal-panel .input-area),
	:global(.ai-scene-modal .modal-panel .input-wrapper),
	:global(.ai-scene-modal .modal-panel .rich-mention-input-wrapper),
	:global(.ai-scene-modal .modal-panel .input-container),
	:global(.ai-scene-modal .modal-panel .input-toolbar),
	:global(.ai-scene-modal .modal-panel .toolbar-left),
	:global(.ai-scene-modal .modal-panel .toolbar-right),
	:global(.ai-scene-modal .modal-panel .send-button),
	:global(.ai-scene-modal .modal-panel .plus-menu-button),
	:global(.ai-scene-modal .modal-panel .menu-wrapper),
	:global(.ai-scene-modal .modal-panel .modal-body) {
		touch-action: none;
	}
	:global(.ai-scene-modal .modal-panel .rich-mention-input) {
		touch-action: pan-y;
	}

	@media (max-width: 768px) {
		:global(.ai-scene-modal .modal-panel .modal-header) {
			padding: 0 16px 12px;
		}

		:global(.ai-scene-modal .modal-panel .close-btn) {
			display: none;
		}
	}
</style>
